import React, { useState, useEffect } from "react";
import { Form, Input, Button, Spin, Row, Col, TimePicker } from "antd";
import { InputNumber } from "antd";
import { ReactSVG } from "react-svg";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import AddressInput from "../../Inputs/AddressInput";
import { getTranslation, setCountryCookie } from "../../../utils/helper";
import { TIME_FORMAT_FRONTEND } from "../../../constants";
import TagInput from "../../Elements/TagInput";
import "./Contact.scss";

const ContactForm = ({ form, isFetching, contact, handleSubmit, handleCancel, countryCodeInit }) => {
  const [countryPhone, setCountryPhone] = useState();
  const [types, setTypes] = useState([]);
  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  useEffect(() => {
    setTypes(contact?.types || []);
  }, [contact]);

  const handleOnFinish = values => {
    const params = { ...values, types };
    params.phone =
      !isEmpty(values.phone) && countryPhone?.dialCode !== values.phone
        ? `${values.phone.includes("+") ? "" : "+"}${values.phone}`
        : null;
    params.duration = values.duration ? Number(values.duration) : null;
    params.startTime = values.startTime ? values.startTime.format(TIME_FORMAT_FRONTEND) : null;
    params.endTime = values.endTime ? values.endTime.format(TIME_FORMAT_FRONTEND) : null;
    params.fields = {};
    values.fields?.forEach(field => {
      params.fields[field.name] = field.value;
    });
    handleSubmit(params);
  };

  const handleOnChangeStartTime = value => {
    form.setFieldsValue({ startTime: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("endTime"))) {
      resetErrorTime();
      return;
    }
    if (form.getFieldValue("endTime") < value) {
      form.setFields([
        {
          name: "startTime",
          errors: ["End Time must be less than Start Time"],
        },
      ]);
    } else {
      resetErrorTime();
      return;
    }
  };

  const handleOnChangeEndTime = value => {
    form.setFieldsValue({ endTime: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("endTime"))) {
      resetErrorTime();
      return;
    }
    if (form.getFieldValue("endTime") > value) {
      form.setFields([
        {
          name: "endTime",
          errors: ["End Time must be greater than Start Time"],
        },
      ]);
    } else {
      resetErrorTime();
      return;
    }
  };

  const resetErrorTime = () => {
    form.setFields([
      {
        name: "startTime",
        errors: [],
      },
      {
        name: "endTime",
        errors: [],
      },
    ]);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="ContactForm">
        <Form
          form={form}
          scrollToFirstError
          layout="vertical"
          hideRequiredMark
          colon={false}
          onFinish={handleOnFinish}
          onKeyPress={e => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}>
          <div className="FormHeader">
            <div className="left">
              {contact
                ? getTranslation(setting, "Contacts", "Edit Contact")
                : getTranslation(setting, "Contacts", "New Contact")}
            </div>
          </div>
          <div className="FormContent">
            <Row gutter={24}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <div className="ContactName">
                  <Form.Item label={getTranslation(setting, "Contacts", "Name")} name="name">
                    <Input size="large" placeholder={getTranslation(setting, "Contacts", "Name")} />
                  </Form.Item>
                </div>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item label={getTranslation(setting, "Contacts", "Phone Number")} name="phone">
                  <PhoneInput
                    country={countryCodeInit}
                    enableSearch
                    disableSearchIcon
                    onChange={(value, country) => {
                      setCountryPhone(country);
                      setCountryCookie(value, country, user);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item label={getTranslation(setting, "Contacts", "Start Time")} name="startTime">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Contacts", "Start Time")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeStartTime}
                    onCalendarChange={handleOnChangeStartTime}
                  />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item label={getTranslation(setting, "Contacts", "End Time")} name="endTime">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Contacts", "End Time")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangeEndTime}
                    onCalendarChange={handleOnChangeEndTime}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              label={getTranslation(setting, "Contacts", "Address")}
              name="address"
              rules={[
                {
                  validator: (_, value) => {
                    if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                      return Promise.resolve();
                    }

                    if (isEmpty(value?.address)) {
                      return Promise.reject(new Error("Required"));
                    }

                    return Promise.reject(new Error("Invalid"));
                  },
                },
              ]}>
              <AddressInput
                size="large"
                placeholder={getTranslation(setting, "Contacts", "Address")}
                onSelect={value => form.setFieldsValue({ address: value })}
              />
            </Form.Item>
            <Form.Item label={getTranslation(setting, "Contacts", "Types")}>
              <TagInput tags={types} onChange={values => setTypes(values)} />
            </Form.Item>
            <Row gutter={24}>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item label={getTranslation(setting, "Contacts", "Duration")} name="duration">
                  <Input size="large" placeholder={getTranslation(setting, "Contacts", "Duration")} suffix="minutes" />
                </Form.Item>
              </Col>
              <Col xs={{ span: 24 }} md={{ span: 12 }}>
                <Form.Item label={getTranslation(setting, "Contacts", "Load")} name="load">
                  <InputNumber
                    size="large"
                    placeholder={getTranslation(setting, "Contacts", "Load")}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={getTranslation(setting, "Contacts", "Notes")} name="notes">
              <Input.TextArea rows={4} placeholder={getTranslation(setting, "Contacts", "Notes")} />
            </Form.Item>
            <div className="ant-col ant-form-item-label">
              <label className="ant-form-item-no-colon">{getTranslation(setting, "Orders", "Custom Fields")}</label>
            </div>
            <Form.List name="fields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(field => (
                    <div key={field.key} className="Field">
                      <div className="FieldName">
                        <Form.Item
                          {...field}
                          name={[field.name, "name"]}
                          rules={[{ required: true, message: "Required" }]}>
                          <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Title")} />
                        </Form.Item>
                      </div>
                      <div className="FieldValue">
                        <Form.Item
                          {...field}
                          name={[field.name, "value"]}
                          rules={[{ required: true, message: "Required" }]}>
                          <Input size="large" placeholder={getTranslation(setting, "Orders", "Field Input")} />
                        </Form.Item>
                      </div>
                      <div className="FieldAction">
                        <Button key="delete" onClick={() => remove(field.name)} size="large">
                          <ReactSVG className="icon" src="/icons/delete.svg" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="primary" onClick={() => add()} className="AddCustomFieldBtn">
                      {getTranslation(setting, "Orders", "Add Custom Field")}
                      <ReactSVG className="icon" src="/icons/add.svg" />
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Button key="cancel" onClick={handleCancel}>
                {getTranslation(setting, "Common", "Cancel")}
              </Button>
              <Button htmlType="submit" type="primary">
                {contact ? getTranslation(setting, "Common", "Update") : getTranslation(setting, "Common", "Create")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default ContactForm;
